import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import type { AudienceReachStats, CountryItem } from '@digiseg-labs/api-client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAudienceBuilderData } from '../../hooks/useAudienceBuilderData';
import { useAudienceBuilderSegments, type AudienceBuilderSegment } from '../../hooks/useAudienceBuilderSegments';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { filterCoreAudienceCategories } from '../../utils/audienceBuilder';
import { getLocaleCountryCode } from '../../utils/locale';
import AudienceBuilderFilters from './AudienceBuilderFilters';
import AudienceBuilderRequestModal from './AudienceBuilderRequestModal';
import AudienceBuilderSummary from './AudienceBuilderSummary';
import type { SelectedAudience } from './types';

const buildReachStats = (
  base: AudienceReachStats | undefined,
  segment: AudienceBuilderSegment | undefined,
  countryReach: CountryItem['reach_stats'] | undefined,
) => {
  const scale = segment?.segment_reach;
  const scaleValue = (value?: number) => (
    scale == null || value == null ? undefined : value * scale
  );

  const derived: AudienceReachStats = {
    num_households: scaleValue(countryReach?.num_households),
    num_persons: scaleValue(countryReach?.num_persons),
    num_devices: scaleValue(countryReach?.num_devices),
    num_daily_impressions: scaleValue(countryReach?.num_daily_impressions),
  };

  const resolved: AudienceReachStats = {
    num_households: base?.num_households ?? derived.num_households,
    num_persons: base?.num_persons ?? derived.num_persons,
    num_devices: base?.num_devices ?? derived.num_devices,
    num_daily_impressions: base?.num_daily_impressions ?? derived.num_daily_impressions,
    in_target_reach: base?.in_target_reach ?? segment?.in_target_reach,
  };

  const hasValue = (
    resolved.num_households !== undefined
    || resolved.num_persons !== undefined
    || resolved.num_devices !== undefined
    || resolved.num_daily_impressions !== undefined
    || resolved.in_target_reach !== undefined
  );

  return hasValue ? resolved : base;
};

const isBrowser = typeof window !== 'undefined';

const AudienceBuilderApp = () => {
  const {
    countries,
    platforms,
    audienceCategories,
    selectedCountry,
    setSelectedCountry,
    isLoadingAudiences,
  } = useTaxonomy({ skipStoredPlatform: true, includeAudiences: true });
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const { segmentMap } = useAudienceBuilderSegments();

  useEffect(() => {
    if (!countries.length || selectedCountry) return;

    let nextCountry: CountryItem | undefined;
    const storedCode = isBrowser ? window.localStorage.getItem('audience:selectedCountry') : null;
    if (storedCode) {
      const normalizedStored = storedCode.toUpperCase();
      nextCountry = countries.find((country) => country.code.toUpperCase() === normalizedStored);
    }

    if (!nextCountry) {
      const localeCode = getLocaleCountryCode();
      if (localeCode) {
        nextCountry = countries.find((country) => country.code.toUpperCase() === localeCode);
      }
    }

    if (!nextCountry) {
      nextCountry = countries.find((country) => (
        country.code === 'GB' || country.code === 'UK'
      )) ?? countries.find((country) => country.display_name.toLowerCase() === 'united kingdom');
    }

    if (!nextCountry) {
      nextCountry = countries[0];
    }

    if (nextCountry) {
      setSelectedCountry(nextCountry);
    }
  }, [countries, selectedCountry, setSelectedCountry]);

  const coreAudienceCategories = useMemo(
    () => filterCoreAudienceCategories(audienceCategories),
    [audienceCategories],
  );
  const defaultAudienceCode = useMemo(() => {
    const matchesTopIncome = (value: string) => {
      const normalized = value.toLowerCase();
      const hasTop20 = /top\s*20%/.test(normalized)
        || normalized.includes('top20')
        || normalized.includes('top 20')
        || normalized.includes('80-100')
        || normalized.includes('80 – 100');
      const hasIncome = normalized.includes('income');
      const hasHousehold = normalized.includes('household') || normalized.includes('hh');
      return hasTop20 && hasIncome && hasHousehold;
    };

    for (const category of coreAudienceCategories) {
      for (const audience of category.audiences) {
        const label = audience.display_name ?? audience.code;
        const code = audience.code;
        if (code.toLowerCase() === 'h5') {
          return audience.code;
        }
        if (matchesTopIncome(label) || matchesTopIncome(code)) {
          return audience.code;
        }
      }
    }

    return null;
  }, [coreAudienceCategories]);
  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);

  const hasAppliedDefaultRef = useRef(false);
  useEffect(() => {
    if (hasAppliedDefaultRef.current) return;
    if (selectedCodes.length > 0) {
      hasAppliedDefaultRef.current = true;
      return;
    }
    if (!defaultAudienceCode) return;
    setSelectedCodes([defaultAudienceCode]);
    hasAppliedDefaultRef.current = true;
  }, [defaultAudienceCode, selectedCodes.length]);

  const selectedAudiences = useMemo<SelectedAudience[]>(() => (
    coreAudienceCategories.flatMap((category) => (
      category.audiences
        .filter((audience) => selectedSet.has(audience.code))
        .map((audience) => {
          const segment = segmentMap.get(audience.code);
          return {
            code: audience.code,
            label: audience.display_name ?? audience.code,
            category: category.display_name ?? category.code,
            reachStats: buildReachStats(
              audience.reach_stats,
              segment,
              selectedCountry?.reach_stats,
            ),
            segmentReach: segment?.segment_reach,
            inTargetReach: segment?.in_target_reach ?? audience.reach_stats?.in_target_reach,
          };
        })
    ))
  ), [coreAudienceCategories, segmentMap, selectedCountry?.reach_stats, selectedSet]);

  const { analysis, sankeyData } = useAudienceBuilderData({ selectedAudiences, selectedCountry });

  const handleToggle = (categoryId: string, code: string) => {
    const category = coreAudienceCategories.find((item) => item.code === categoryId);
    if (!category) return;

    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }

      const categoryCodes = category.audiences.map((audience) => audience.code);
      const allSelected = categoryCodes.every((optionCode) => next.has(optionCode));
      if (allSelected) {
        categoryCodes.forEach((optionCode) => next.delete(optionCode));
      }

      return Array.from(next);
    });
  };

  const handleRequest = () => {
    if (!selectedAudiences.length || !selectedCountry) return;
    setIsRequestOpen(true);
  };

  return (
    <Box className="builder-shell">
      <Flex direction="column" gap="2" className="builder-hero">
        <Heading size="7">Audience Builder</Heading>
        <Text size="3" color="gray">
          Unlock the scale of Digiseg data and build your own audience in minutes.
        </Text>
      </Flex>

      <Box className="builder-grid">
        <AudienceBuilderFilters
          categories={coreAudienceCategories}
          selectedCodes={selectedSet}
          onToggle={handleToggle}
          countries={countries}
          selectedCountry={selectedCountry}
          onCountryChange={setSelectedCountry}
          isLoadingAudiences={isLoadingAudiences}
        />
        <AudienceBuilderSummary
          selectedAudiences={selectedAudiences}
          analysis={analysis}
          sankeyLinks={sankeyData.links}
          sankeyNodes={sankeyData.nodes}
          selectedCountry={selectedCountry}
          onRequest={handleRequest}
        />
      </Box>

      <AudienceBuilderRequestModal
        open={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        selectedAudiences={selectedAudiences}
        countries={countries}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
        platforms={platforms}
        analysis={analysis}
        sankeyLinks={sankeyData.links}
        sankeyNodes={sankeyData.nodes}
      />
    </Box>
  );
};

export default AudienceBuilderApp;
